<template>
  <section class="soft-extract-workspace">
    <div class="soft-extract-main">
      <aside class="extract-settings">
        <section class="setting-section">
          <header class="section-title">
            <span>01</span>
            <div>
              <strong>上传室内效果图</strong>
              <small>建议画面清晰、软装陈设完整，支持 PNG / JPG / WebP</small>
            </div>
          </header>

          <label class="image-dropzone" :class="{ filled: previewUrl }">
            <input
              type="file"
              accept="image/png,image/jpeg,image/webp"
              aria-label="选择室内效果图"
              @change="handleFile"
            />
            <img
              v-if="previewUrl"
              :src="$ossImageUrl(previewUrl, 'preview')"
              alt="待提取软装方案的室内效果图"
              decoding="async"
            />
            <span v-else class="dropzone-empty">
              <i class="el-icon-upload2"></i>
              <strong>选择室内效果图</strong>
              <small>客厅、卧室、餐厅及商业空间均可分析</small>
            </span>
            <button v-if="previewUrl" type="button" class="replace-image">更换图片</button>
          </label>

          <div class="file-rule" :class="{ valid: previewUrl }">
            <i :class="previewUrl ? 'el-icon-circle-check' : 'el-icon-warning-outline'"></i>
            <span>{{ previewUrl ? sourceFileName : '请上传单张完整效果图，不要上传拼图或纯平面图' }}</span>
          </div>
        </section>

        <section class="setting-section">
          <header class="section-title">
            <span>02</span>
            <div>
              <strong>设置提取内容</strong>
              <small>提取项会按照专业提案板的阅读顺序自动排版</small>
            </div>
          </header>

          <div class="output-row">
            <span>内容密度</span>
            <el-select v-model="detailLevel" size="small" aria-label="选择软装方案内容密度">
              <el-option label="标准提案 · 推荐" value="标准提案" />
              <el-option label="精简概览 · 更清爽" value="精简概览" />
              <el-option label="详细清单 · 信息丰富" value="详细清单" />
            </el-select>
          </div>

          <div class="extract-items" aria-label="方案提取内容">
            <span v-for="item in extractItems" :key="item.label">
              <i :class="item.icon"></i>{{ item.label }}
            </span>
          </div>

          <div class="output-row dimension-toggle-row">
            <div>
              <span>参考尺寸</span>
              <small>按画面比例给出合理区间，不虚构精确值</small>
            </div>
            <el-switch
              v-model="includeDimensions"
              active-color="#2563eb"
              aria-label="是否显示软装单品参考尺寸"
            />
          </div>

          <label class="note-field">
            <span>补充要求 <em>{{ extractionNote.length }}/300</em></span>
            <textarea
              v-model.trim="extractionNote"
              maxlength="300"
              placeholder="例如：重点分析沙发、茶几和装饰画，补充采购搭配建议"
            ></textarea>
            <AiPromptOptimizer
              v-model="extractionNote"
              :disabled="generating"
              :max-length="300"
              context="室内软装提取、分析和采购搭配建议的补充要求"
            />
          </label>
        </section>

        <section class="setting-section output-section">
          <header class="section-title">
            <span>03</span>
            <div>
              <strong>方案板输出</strong>
              <small>默认跟随原图画幅，也可以手动调整</small>
            </div>
          </header>
          <div class="output-row">
            <span>画幅</span>
            <AspectRatioSelector
              v-model="selectedRatio"
              compact
              :disabled="generating"
              :options="ratioOptions"
              aria-label="选择方案板画幅"
            />
          </div>
          <div class="output-row">
            <span>分辨率</span>
            <ResolutionSelector
              v-model="selectedResolution"
              compact
              :disabled="generating"
              :show-label="false"
            />
          </div>
          <div class="output-row">
            <span>生成质量</span>
            <QualitySelector v-model="selectedQuality" compact :disabled="generating" />
          </div>
        </section>

        <button class="generate-report" :disabled="generating" @click="generateExtraction">
          <span v-if="generating" class="button-spinner"></span>
          <i v-else class="el-icon-data-analysis"></i>
          <span>{{ generating ? generationText : '生成软装方案提取图' }}</span>
          <small v-if="!generating">分析图片并生成 1 张完整方案板</small>
        </button>
        <div v-if="generating" class="generation-progress" aria-live="polite">
          <i :style="{ width: `${progress}%` }"></i>
        </div>
      </aside>

      <section class="report-panel">
        <header class="report-head">
          <div>
            <strong>软装方案提取图</strong>
            <span>结果包含空间概览、核心色板、材质肌理、关键单品与搭配建议</span>
          </div>
          <div class="report-actions">
            <WorkflowTuningButton
              v-if="resultUrl"
              variant="button"
              icon-only
              :image-url="resultUrl"
              :prompt="submittedPrompt || finalPrompt"
              source-model="gpt-image-2-official"
              module-name="软装方案提取"
            />
            <button
              v-if="resultUrl"
              type="button"
              title="下载软装方案提取图"
              aria-label="下载软装方案提取图"
              @click="downloadResult"
            ><i class="el-icon-download"></i></button>
            <button
              v-if="resultUrl && floorPlanFile"
              type="button"
              title="重新生成"
              aria-label="重新生成"
              :disabled="generating"
              @click="generateExtraction"
            ><i class="el-icon-refresh"></i></button>
          </div>
        </header>

        <div class="report-stage" :class="{ 'has-source': previewUrl, 'has-result': resultUrl }">
          <article class="source-preview">
            <span class="stage-label">原始效果图</span>
            <img
              v-if="previewUrl"
              class="zoomable-stage-image"
              :src="$ossImageUrl(previewUrl, 'preview')"
              alt="用于软装方案提取的原始效果图"
              title="点击放大原始效果图"
              role="button"
              tabindex="0"
              decoding="async"
              @click="previewImage(previewUrl, '软装方案提取 · 原始效果图', currentViewerItems, 0)"
              @keydown.enter.prevent="previewImage(previewUrl, '软装方案提取 · 原始效果图', currentViewerItems, 0)"
              @keydown.space.prevent="previewImage(previewUrl, '软装方案提取 · 原始效果图', currentViewerItems, 0)"
            />
            <div v-else class="stage-empty">
              <i class="el-icon-picture-outline"></i>
              <strong>等待效果图</strong>
              <span>上传后可在这里核对原始空间与软装陈设</span>
            </div>
          </article>

          <article class="report-preview">
            <span class="stage-label">AI 方案板</span>
            <img
              v-if="resultUrl"
              class="zoomable-stage-image"
              :src="$ossImageUrl(resultUrl, 'preview')"
              alt="AI生成的专业软装方案提取图"
              title="点击放大软装方案提取图"
              role="button"
              tabindex="0"
              decoding="async"
              @click="previewImage(resultUrl, '软装方案提取 · AI 方案板', currentViewerItems, previewUrl ? 1 : 0)"
              @keydown.enter.prevent="previewImage(resultUrl, '软装方案提取 · AI 方案板', currentViewerItems, previewUrl ? 1 : 0)"
              @keydown.space.prevent="previewImage(resultUrl, '软装方案提取 · AI 方案板', currentViewerItems, previewUrl ? 1 : 0)"
            />
            <div v-else-if="generating" class="stage-empty report-loading">
              <span class="report-loader" aria-hidden="true">
                <i></i><i></i><i></i>
              </span>
              <strong>{{ generationText }}</strong>
              <span>正在识别配色、材质、家具与饰品，并建立方案板版式</span>
            </div>
            <div v-else class="report-blueprint" aria-hidden="true">
              <div class="blueprint-title"></div>
              <div class="blueprint-grid">
                <span class="blueprint-hero"><i></i></span>
                <span class="blueprint-detail"><i></i><i></i><i></i></span>
                <span class="blueprint-palette"><i></i><i></i><i></i><i></i><i></i></span>
                <span class="blueprint-material"><i></i><i></i><i></i><i></i><i></i></span>
                <span class="blueprint-cards"><i></i><i></i><i></i><i></i></span>
              </div>
              <strong>等待生成专业方案板</strong>
              <small>一张图看懂空间的软装搭配逻辑</small>
            </div>
          </article>
        </div>

        <div class="quality-strip">
          <span><i class="el-icon-check"></i> 只分析可见内容</span>
          <span><i class="el-icon-check"></i> 色彩比例清晰</span>
          <span><i class="el-icon-check"></i> 材质与肌理归类</span>
          <span><i class="el-icon-check"></i> 关键单品独立展示</span>
          <span><i class="el-icon-check"></i> 中文专业排版</span>
        </div>

        <section v-if="submittedPrompt" class="submitted-prompt">
          <header>
            <div>
              <span>ACTUAL AI PROMPT</span>
              <strong>本次实际发送给 AI 的提示词</strong>
            </div>
            <button
              type="button"
              title="复制实际发送的提示词"
              aria-label="复制实际发送的提示词"
              @click="copyText(submittedPrompt)"
            ><i class="el-icon-copy-document"></i>复制</button>
          </header>
          <pre>{{ submittedPrompt }}</pre>
        </section>

        <div v-if="errorMessage" class="generation-error" role="alert">
          <i class="el-icon-warning-outline"></i>
          <div>
            <strong>{{ errorMessage }}</strong>
            <span>{{ errorSuggestion }}</span>
          </div>
          <button
            v-if="(currentBatchId || currentTaskIds.length) && errorRetryable"
            type="button"
            :disabled="generating"
            @click="continueTask"
          >查询任务状态</button>
        </div>
      </section>
    </div>

    <section class="method-panel">
      <header class="method-head">
        <div>
          <span class="workspace-kicker">EXTRACTION METHOD</span>
          <h2>方案板会提取哪些设计信息</h2>
        </div>
        <button type="button" class="prompt-toggle" @click="promptVisible = !promptVisible">
          <i :class="promptVisible ? 'el-icon-arrow-up' : 'el-icon-arrow-down'"></i>
          {{ promptVisible ? '收起提示词' : '查看对应提示词' }}
        </button>
      </header>

      <div class="method-steps">
        <article v-for="item in methodSteps" :key="item.number">
          <span>{{ item.number }}</span>
          <i :class="item.icon"></i>
          <div><strong>{{ item.title }}</strong><p>{{ item.description }}</p></div>
        </article>
      </div>

      <transition name="prompt-reveal">
        <div v-if="promptVisible" class="prompt-content">
          <section class="prompt-block">
            <header>
              <div><span>SYSTEM PROMPT</span><strong>软装方案提取系统提示词</strong></div>
              <button type="button" title="复制系统提示词" aria-label="复制系统提示词" @click="copyText(systemPrompt)">
                <i class="el-icon-copy-document"></i>
              </button>
            </header>
            <pre>{{ systemPrompt }}</pre>
          </section>
          <section class="prompt-block">
            <header>
              <div><span>FINAL PROMPT</span><strong>本次提交给图片模型的提示词</strong></div>
              <button type="button" title="复制最终提示词" aria-label="复制最终提示词" @click="copyText(finalPrompt)">
                <i class="el-icon-copy-document"></i>
              </button>
            </header>
            <pre>{{ finalPrompt }}</pre>
          </section>
          <section class="negative-constraints">
            <strong>提取原则</strong>
            <span v-for="item in constraints" :key="item">{{ item }}</span>
          </section>
        </div>
      </transition>
    </section>

    <section class="extraction-history">
      <header class="history-section-head">
        <div>
          <span class="workspace-kicker">GENERATION RECORDS</span>
          <h2>软装方案提取记录</h2>
          <p>展示该功能的全部生成记录、原图、结果图及每次实际发送给 AI 的提示词；点击图片可放大。</p>
        </div>
        <button type="button" class="history-refresh" :disabled="historyLoading" @click="loadPendingTask">
          <i :class="historyLoading ? 'el-icon-loading' : 'el-icon-refresh'"></i>
          {{ historyLoading ? '正在刷新' : '刷新记录' }}
        </button>
      </header>

      <div v-if="history.length" class="extraction-history-grid">
        <article v-for="record in history" :key="record.id" :class="{ active: currentBatchId === record.id }">
          <div class="history-comparison">
            <span class="history-status" :class="record.status">{{ statusText(record.status) }}</span>
            <button
              type="button"
              class="history-figure"
              :disabled="!record.source"
              aria-label="放大查看该次软装提取原图"
              @click="previewHistoryImage(record, 'source')"
            >
              <img
                v-if="record.source"
                :src="$ossImageUrl(record.source, 'thumbnail')"
                alt="历史软装提取原图"
                loading="lazy"
                decoding="async"
              />
              <span v-else><i class="el-icon-picture-outline"></i>原图不可用</span>
              <span class="history-caption">原始效果图</span>
              <i v-if="record.source" class="el-icon-zoom-in history-zoom" aria-hidden="true"></i>
            </button>
            <button
              type="button"
              class="history-figure"
              :disabled="!record.result"
              :aria-label="record.result ? '放大查看该次软装方案提取结果' : statusText(record.status)"
              @click="previewHistoryImage(record, 'result')"
            >
              <img
                v-if="record.result"
                :src="$ossImageUrl(record.result, 'thumbnail')"
                alt="历史软装方案提取结果"
                loading="lazy"
                decoding="async"
              />
              <span v-else>
                <i :class="record.status === 'generating' ? 'el-icon-loading' : 'el-icon-warning-outline'"></i>
                {{ statusText(record.status) }}
              </span>
              <span class="history-caption">AI 方案板</span>
              <i v-if="record.result" class="el-icon-zoom-in history-zoom" aria-hidden="true"></i>
            </button>
          </div>
          <ImageDimensionsRow :items="[{ label: '原图尺寸', url: record.source, probe: true }, { label: '结果尺寸', url: record.result, width: record.resultWidth, height: record.resultHeight }]" />

          <GenerationModelDetails :record="record" />
          <GenerationFailureDetails :record="record" />
          <div class="history-record-meta">
            <div>
              <strong>{{ record.style || '软装方案提取图' }}</strong>
              <dl class="history-record-details">
                <div>
                  <dt>图片比例</dt>
                  <dd>{{ record.size || '16:9' }}</dd>
                </div>
                <div>
                  <dt>生成时间</dt>
                  <dd><time class="creation-date" :datetime="record.createdAt || undefined">{{ formatRecordTime(record.createdAt) }}</time></dd>
                </div>
              </dl>
            </div>
            <WorkflowTuningButton
              v-if="record.result"
              variant="button"
              icon-only
              :image-url="record.result"
              :prompt="record.prompt || submittedPrompt || finalPrompt"
              :source-model="record.model || ''"
              module-name="软装方案提取"
            />
            <button
              v-if="record.result"
              type="button"
              title="下载这张软装方案提取图"
              aria-label="下载这张软装方案提取图"
              @click="downloadHistoryResult(record)"
            ><i class="el-icon-download"></i></button>
          </div>

          <div class="history-record-prompt">
            <header>
              <strong>实际发送提示词</strong>
              <button
                type="button"
                :disabled="!record.prompt"
                title="复制该次实际提示词"
                aria-label="复制该次实际提示词"
                @click="copyText(record.prompt)"
              ><i class="el-icon-copy-document"></i></button>
            </header>
            <pre>{{ record.prompt || '该记录未保存提示词' }}</pre>
          </div>
        </article>
      </div>

      <div v-else class="history-empty">
        <i :class="historyLoading ? 'el-icon-loading' : 'el-icon-time'"></i>
        <strong>{{ historyLoading ? '正在加载生成记录' : '还没有软装方案提取记录' }}</strong>
        <span>{{ historyError || '完成第一次生成后，原图、结果图和实际提示词会自动显示在这里。' }}</span>
      </div>
      <AppPagination :page="historyPage" :page-size="12" :total="historyTotal" :loading="historyLoading" @change="changeHistoryPage" />
    </section>
  </section>
</template>

<script>
/* 软装提取把效果图拆成色板、材质和单品信息图；结果是独立生成批次，仍需按 batchId 轮询。 */
import { apiRequest, downloadResource } from '@/services/api'
import AspectRatioSelector from '@/components/AspectRatioSelector.vue'
import ResolutionSelector from '@/components/ResolutionSelector.vue'
import QualitySelector from '@/components/QualitySelector.vue'
import ImageDimensionsRow from '@/components/ImageDimensionsRow.vue'
import AiPromptOptimizer from '@/components/AiPromptOptimizer.vue'
import { mergePendingGenerations, removePendingGeneration, savePendingGeneration } from '@/utils/pendingGenerationRecords'
import { DEFAULT_GENERATION_QUALITY, MINIMUM_OUTPUT_RESOLUTION } from '@/utils/outputQuality'
import { closestImageRatio, SUPPORTED_IMAGE_RATIOS } from '@/utils/imageAspectRatio'

const EXTRACTION_STAGE = 'soft_decoration_extract'
const FALLBACK_SYSTEM_PROMPT = `你是资深室内软装设计师、材料顾问和专业提案板视觉设计师。你的任务是分析用户上传的单张室内效果图，生成一张中文“软装方案提取图”。

强制规则：
1. 只提取原图真实可见或可以谨慎判断的内容，不重新设计空间，不新增不存在的家具、材质、品牌、型号或装饰。
2. 输出是一张完整的专业信息图，而不是重新生成的室内效果图。使用温暖的白色或米白色背景、清晰网格、充足留白和统一圆角卡片，视觉克制、现代、适合设计汇报。
3. 方案板必须包含：原空间缩略图与风格概览、局部设计语言拼贴、5色核心色板及近似占比、5类材质与肌理、6至8个关键软装单品卡片、3条搭配建议。
4. 关键单品卡片从原图中提取主体并使用干净浅色背景展示，写明通用品类名称、可见特征、材质/颜色、设计亮点；只有能够合理估算时才给参考尺寸区间，并明确“参考尺寸”。
5. 所有标题和说明使用简体中文，文字短、清楚、字号足够大，避免密集长段落；色板可补充 HEX 近似色值与占比。
6. 保持原图配色、材质和单品特征，不擅自替换风格；无法确认的细节写“依据画面估算”或省略，不得臆造品牌和精确参数。
7. 只输出一张最终方案提取图，不输出解释过程，不添加 Logo、水印、二维码或无关装饰。`

export default {
  name: 'SoftDecorationExtract',
  components: { AspectRatioSelector, ResolutionSelector, QualitySelector, ImageDimensionsRow, AiPromptOptimizer },
  data() {
    return {
      floorPlanFile: null,
      previewUrl: '',
      sourceFileName: '',
      detailLevel: '标准提案',
      includeDimensions: true,
      extractionNote: '',
      selectedRatio: '16:9',
      ratioOptions: SUPPORTED_IMAGE_RATIOS,
      selectedResolution: MINIMUM_OUTPUT_RESOLUTION,
      selectedQuality: DEFAULT_GENERATION_QUALITY,
      apiConfigured: false,
      generating: false,
      progress: 0,
      generationText: '准备分析效果图',
      resultUrl: '',
      errorMessage: '',
      errorSuggestion: '',
      errorRetryable: false,
      currentBatchId: '',
      currentTaskIds: [],
      requestControllers: [],
      pollingStopped: false,
      recoveryTimer: null,
      promptVisible: false,
      systemPrompt: FALLBACK_SYSTEM_PROMPT,
      submittedPrompt: '',
      history: [],
      historyLoading: false,
      historyPage: 1,
      historyTotal: 0,
      historyError: '',
      extractItems: [
        { icon: 'el-icon-house', label: '风格概览' },
        { icon: 'el-icon-brush', label: '核心色板' },
        { icon: 'el-icon-collection-tag', label: '材质肌理' },
        { icon: 'el-icon-refrigerator', label: '家具单品' },
        { icon: 'el-icon-sunny', label: '灯具饰品' },
        { icon: 'el-icon-document-checked', label: '搭配建议' }
      ],
      methodSteps: [
        { number: '01', icon: 'el-icon-view', title: '识别空间语言', description: '判断整体风格、构图重心、色彩关系与软装层次。' },
        { number: '02', icon: 'el-icon-brush', title: '提取色材系统', description: '形成核心色板，并归纳布艺、木材、金属、石材等肌理。' },
        { number: '03', icon: 'el-icon-files', title: '拆解关键单品', description: '提取家具、灯具、地毯、装饰画与摆件的可见特征。' },
        { number: '04', icon: 'el-icon-data-board', title: '生成专业方案板', description: '以清晰中文层级排版，并补充可执行的整体搭配建议。' }
      ],
      constraints: [
        '不改原方案', '不臆造品牌', '不伪造精确尺寸', '只提取可见内容',
        '色值标注为近似值', '单品使用通用名称', '中文清晰可读', '不输出水印'
      ]
    }
  },
  computed: {
    currentViewerItems() {
      return [
        this.previewUrl ? { src: this.previewUrl, title: '软装方案提取 · 原始效果图' } : null,
        this.resultUrl ? { src: this.resultUrl, title: '软装方案提取 · AI 方案板' } : null
      ].filter(Boolean)
    },
    densityInstruction() {
      if (this.detailLevel === '精简概览') {
        return '采用精简概览密度：突出风格、5色配色、5类材质和6个核心单品，说明尽量短。'
      }
      if (this.detailLevel === '详细清单') {
        return '采用详细清单密度：提取8个关键单品，增加材质、颜色、造型和搭配逻辑，但保持版面可读。'
      }
      return '采用标准提案密度：提取6至8个关键单品，信息完整但不过度拥挤。'
    },
    dimensionInstruction() {
      return this.includeDimensions
        ? '对可合理判断的单品给出宽×深×高的参考尺寸区间，统一标记“参考尺寸/依据画面估算”；无法判断时省略。'
        : '不要输出任何尺寸数字，重点描述单品的比例、造型、材质和设计亮点。'
    },
    finalPrompt() {
      const note = this.extractionNote || '无额外要求，完整分析画面中的主要软装搭配'
      return `【生成专业软装方案提取图】
以本轮上传的单张室内效果图为唯一依据，先在内部分析，再输出一张完整中文信息图。${this.densityInstruction}

版面结构：
1. 顶部标题：根据画面概括“空间风格软装方案（软装方案提取图）”。
2. 左上“空间氛围概览”：保留原图缩略图，旁边用3至5个局部拼贴展示家具造型、纹理和饰品语言。
3. 右上“核心色彩提取”：5个横向色块，写简体中文色名、近似 HEX 色值和画面占比，合计约100%。
4. 右中“材质与肌理方案”：5个圆形或方形材质样本，标注材质大类与画面可见特征。
5. 下方“关键单品清单”：每个卡片包含干净浅色背景的单品提取图、通用品类名称、可见材质/颜色和一句设计亮点。${this.dimensionInstruction}
6. 右下“搭配建议”：3条简短建议，围绕整体色调统一、材质层次和艺术品/饰品摆放。

视觉规范：${this.selectedRatio}画幅，温暖白色背景，现代室内设计提案风格，网格对齐，留白充足，细线分隔，米灰、木色与香槟金作为辅助色。所有文字必须是简体中文且清晰可读，不使用英文段落。
补充要求：${note}。
严禁重新设计原空间，严禁新增原图没有的单品，严禁臆造品牌、型号、价格和精确尺寸，严禁 Logo、水印、二维码、错别字密集的长文。只输出一张最终软装方案提取图。`
    }
  },
  created() {
    this.loadConfiguration()
    this.loadPendingTask()
  },
  beforeDestroy() {
    this.pollingStopped = true
    if (this.recoveryTimer) window.clearTimeout(this.recoveryTimer)
    this.requestControllers.forEach(controller => controller.abort())
    if (this.previewUrl && this.previewUrl.startsWith('blob:')) URL.revokeObjectURL(this.previewUrl)
  },
  methods: {
    previewImage(image, title, items = [], index = null) {
      if (!image) return
      this.$emit('preview-image', image, { title, items, index })
    },
    async request(url, options = {}) {
      return apiRequest(url, options, {
        controllers: this.requestControllers,
        isStopped: () => this.pollingStopped
      })
    },
    async loadConfiguration() {
      try {
        const health = await this.request('/api/health')
        this.apiConfigured = Boolean(health.configured)
      } catch (error) {
        this.apiConfigured = false
      }
      try {
        const response = await this.request('/api/prompts/soft-decoration-extract')
        if (response.systemPrompt) this.systemPrompt = response.systemPrompt
      } catch (error) {
        this.systemPrompt = FALLBACK_SYSTEM_PROMPT
      }
    },
    handleFile(event) {
      const file = event.target.files && event.target.files[0]
      event.target.value = ''
      if (!file) return
      if (!['image/png', 'image/jpeg', 'image/webp'].includes(file.type)) {
        this.showMessage('仅支持 PNG、JPG 或 WebP 效果图', 'warning')
        return
      }
      if (file.size >= 10 * 1024 * 1024) {
        this.showMessage('效果图必须小于 10MB，请压缩后再上传', 'warning')
        return
      }
      const objectUrl = URL.createObjectURL(file)
      const image = new Image()
      image.onload = () => {
        if (this.previewUrl && this.previewUrl.startsWith('blob:')) URL.revokeObjectURL(this.previewUrl)
        this.floorPlanFile = file
        this.previewUrl = objectUrl
        this.sourceFileName = file.name
        this.selectedRatio = closestImageRatio(image.naturalWidth, image.naturalHeight, this.ratioOptions)
        this.resetGenerationState()
      }
      image.onerror = () => {
        URL.revokeObjectURL(objectUrl)
        this.showMessage('效果图文件已损坏或无法读取，请重新选择', 'error')
      }
      image.src = objectUrl
    },
    resetGenerationState() {
      this.resultUrl = ''
      this.errorMessage = ''
      this.errorSuggestion = ''
      this.errorRetryable = false
      this.currentBatchId = ''
      this.currentTaskIds = []
      this.progress = 0
    },
    async uploadSource() {
      const form = new FormData()
      form.append('file', this.floorPlanFile)
      return this.request('/api/images/upload', { method: 'POST', body: form, timeoutMs: 120000 })
    },
    taskImage(task) {
      const data = task && task.result && task.result.data
      return Array.isArray(data) && data[0] ? data[0].url : ''
    },
    async pollTask(taskId) {
      const startedAt = Date.now()
      let failures = 0
      while (Date.now() - startedAt < 600000 && !this.pollingStopped) {
        try {
          const task = await this.request(`/api/generations/${encodeURIComponent(taskId)}`, {
            timeoutMs: 12000,
            retries: 1,
            timeoutMessage: '方案提取任务状态查询超时'
          })
          failures = 0
          this.progress = Math.max(this.progress, Math.min(96, Number(task.progress || 0)))
          if (task.status === 'completed') {
            const url = this.taskImage(task)
            if (!url) {
              const error = new Error('任务已完成，但未返回可用的方案提取图')
              error.suggestion = '请联系管理员并提供任务编号，原任务记录已保留。'
              error.retryable = false
              throw error
            }
            return url
          }
          if (task.status === 'failed') {
            const error = new Error(task.error && task.error.message ? task.error.message : 'AI 软装方案提取失败')
            Object.assign(error, task.error || {}, { terminal: true })
            throw error
          }
          this.generationText = this.progress > 58
            ? `正在排版方案板 ${this.progress}%`
            : `正在提取软装信息 ${this.progress}%`
        } catch (error) {
          if (error.terminal || error.retryable === false) throw error
          failures += 1
          this.generationText = `网络波动，正在恢复任务进度（${failures}/5）`
          if (failures >= 5) {
            const pending = new Error('暂时无法查询生成进度，但任务仍可能在后台处理')
            pending.suggestion = '请恢复网络后点击“查询任务状态”，无需重新提交。'
            pending.retryable = true
            pending.pending = true
            throw pending
          }
        }
        await new Promise(resolve => window.setTimeout(resolve, 3000))
      }
      const pending = new Error('方案板生成时间较长，任务仍在后台处理中')
      pending.suggestion = '请稍后点击“查询任务状态”，无需重复创建任务。'
      pending.retryable = true
      pending.pending = true
      throw pending
    },
    async waitForTaskAssignment(batchId) {
      for (let attempt = 0; attempt < 20 && !this.pollingStopped; attempt += 1) {
        const record = await this.request(`/api/records/${encodeURIComponent(batchId)}`, {
          timeoutMs: 15000,
          retries: 1,
          timeoutMessage: '正在等待 AI 服务确认任务'
        })
        if (record.status === 'failed') {
          const failed = (record.tasks || []).find(task => task && task.status === 'failed')
          const details = failed && failed.error
          const error = new Error((details && details.message) || record.error || '软装方案提取任务提交失败')
          error.suggestion = (details && details.suggestion) || '请稍后重新创建任务。'
          error.retryable = Boolean(details && details.retryable)
          error.terminal = true
          throw error
        }
        if (record.status === 'completed' && record.images && record.images[0]) {
          this.progress = 100
          return { resultUrl: record.images[0].url, taskIds: [] }
        }
        const taskIds = (record.tasks || []).map(task => task && task.id).filter(Boolean)
        if (taskIds.length) return { resultUrl: '', taskIds }
        this.progress = Math.max(this.progress, 13 + attempt)
        this.generationText = 'AI 服务正在确认任务'
        await new Promise(resolve => window.setTimeout(resolve, 1500))
      }
      const pending = new Error('任务已保存，AI 服务正在后台确认任务编号')
      pending.suggestion = '系统会继续自动查询，请稍后点击“查询任务状态”，无需重复提交。'
      pending.retryable = true
      pending.pending = true
      throw pending
    },
    async generateExtraction() {
      if (this.generating) return
      if (!this.floorPlanFile) {
        this.showMessage('请先上传一张完整室内效果图', 'warning')
        return
      }
      if (!this.apiConfigured) {
        this.showMessage('请先配置 ToAPIs API Key', 'warning')
        return
      }
      this.generating = true
      this.errorMessage = ''
      this.errorSuggestion = ''
      this.errorRetryable = false
      this.resultUrl = ''
      this.progress = 4
      this.generationText = '正在上传效果图'
      const random = window.crypto && window.crypto.randomUUID
        ? window.crypto.randomUUID()
        : `${Date.now()}-${Math.random().toString(36).slice(2, 14)}`
      const requestId = `soft-extract-${random}`.slice(0, 64)
      const pendingId = `interior-${requestId}`
      const createdAt = new Date().toISOString()
      const submittedPrompt = this.finalPrompt
      this.submittedPrompt = submittedPrompt
      this.savePendingTask(pendingId, createdAt, '', submittedPrompt)
      try {
        const asset = await this.uploadSource()
        this.savePendingTask(pendingId, createdAt, asset.localUrl, submittedPrompt)
        this.progress = 12
        this.generationText = '正在创建方案提取任务'
        const batch = await this.request('/api/generations', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            imageUrls: [asset.url],
            prompt: submittedPrompt,
            space: '原效果图空间',
            style: '软装方案提取图',
            size: this.selectedRatio,
            resolution: this.selectedResolution,
            quality: this.selectedQuality,
            count: 1,
            stage: EXTRACTION_STAGE,
            floorPlanRecordUrls: asset.localUrl ? [asset.localUrl] : [],
            referenceRecordUrls: [],
            requestId
          }),
          timeoutMs: 120000,
          retries: 1,
          timeoutMessage: '创建方案提取任务等待超时'
        })
        this.submittedPrompt = batch.prompt || submittedPrompt
        this.currentBatchId = batch.batchId || batch.id || pendingId
        removePendingGeneration(pendingId)
        const assignment = await this.waitForTaskAssignment(this.currentBatchId)
        this.currentTaskIds = assignment.taskIds
        if (assignment.resultUrl) {
          this.resultUrl = assignment.resultUrl
        } else {
          this.progress = Math.max(this.progress, 20)
          this.resultUrl = await this.pollTask(this.currentTaskIds[0])
        }
        this.progress = 100
        this.generationText = '软装方案提取图已生成'
        this.showMessage('软装方案提取图已生成', 'success')
      } catch (error) {
        this.errorMessage = error.message || '软装方案提取失败，请稍后重试'
        this.errorSuggestion = error.suggestion || '请检查效果图和网络后重试；若任务已创建，请优先查询任务状态。'
        this.errorRetryable = Boolean(error.pending || error.retryable)
        if (error.pending || error.retryable) this.scheduleRecovery()
        else if (!this.currentBatchId) removePendingGeneration(pendingId)
        this.showMessage(this.errorMessage, error.pending ? 'warning' : 'error')
      } finally {
        this.generating = false
        this.loadPendingTask()
      }
    },
    savePendingTask(id, createdAt, sourceUrl = '', prompt = this.finalPrompt) {
      savePendingGeneration({
        id,
        space: '原效果图空间',
        style: '软装方案提取图',
        size: this.selectedRatio,
        prompt,
        status: 'generating',
        stage: EXTRACTION_STAGE,
        progress: 0,
        expectedCount: 1,
        tasks: [],
        images: [],
        inputContext: {
          floorPlanImages: sourceUrl ? [{ type: 'original', label: '原始效果图', url: sourceUrl }] : [],
          referenceImages: [],
          annotation: null
        },
        createdAt
      })
    },
    async loadPendingTask() {
      if (this.historyLoading) return
      this.historyLoading = true
      this.historyError = ''
      try {
        const response = await this.request(`/api/records/paged?stage=${encodeURIComponent(EXTRACTION_STAGE)}&page=${this.historyPage}&pageSize=12`)
        this.historyTotal = Number(response.total || 0)
        const records = response.items || []
        const matches = (this.historyPage === 1 ? mergePendingGenerations(records, EXTRACTION_STAGE) : records)
          .filter(record =>
            record.stage === EXTRACTION_STAGE ||
            (record.stage === 'concept' && String(record.id || '').startsWith('interior-soft-extract-'))
          )
        this.history = matches.slice(0, 12).map(record => ({
          id: record.id,
          style: record.style,
          size: record.size,
          prompt: record.prompt || '',
          status: record.status,
          createdAt: record.createdAt,
          source: record.inputContext && record.inputContext.floorPlanImages && record.inputContext.floorPlanImages[0]
            ? record.inputContext.floorPlanImages[0].url
            : '',
          result: record.images && record.images[0] ? (record.images[0].url || record.images[0]) : '',
          resultWidth: record.images && record.images[0] ? record.images[0].width : null,
          resultHeight: record.images && record.images[0] ? record.images[0].height : null,
          taskIds: (record.tasks || []).map(task => task && task.id).filter(Boolean),
          taskStates: record.tasks || [],
          errorMessage: record.error || record.errorMessage || ''
          , imageModel: record.imageModel || ''
        }))
        const active = matches.find(record => record.status === 'generating')
        if (!active) return
        const source = active.inputContext && active.inputContext.floorPlanImages
          ? active.inputContext.floorPlanImages[0]
          : null
        this.currentBatchId = active.id
        this.currentTaskIds = (active.tasks || []).map(task => task && task.id).filter(Boolean)
        this.submittedPrompt = active.prompt || this.submittedPrompt
        if (!this.previewUrl && source && source.url) {
          this.previewUrl = source.url
          this.sourceFileName = '历史任务原图'
        }
        this.progress = Math.max(12, Number(active.progress || 0))
        this.scheduleRecovery(300)
      } catch (error) {
        this.historyError = `${error.message || '暂时无法加载软装方案提取记录'}。请检查网络后重试。`
      } finally {
        this.historyLoading = false
      }
    },
    scheduleRecovery(delay = 2500) {
      if (this.recoveryTimer) window.clearTimeout(this.recoveryTimer)
      if (this.pollingStopped) return
      this.recoveryTimer = window.setTimeout(() => {
        this.recoveryTimer = null
        this.continueTask(true)
      }, delay)
    },
    changeHistoryPage(page) {
      this.historyPage = page
      this.loadPendingTask()
    },
    async continueTask(silent = false) {
      if (this.generating) return
      if (this.currentTaskIds.length) return this.continuePolling(silent)
      if (!this.currentBatchId) return
      this.generating = true
      this.errorMessage = ''
      this.errorSuggestion = ''
      this.errorRetryable = false
      this.generationText = '正在确认后台任务'
      try {
        const assignment = await this.waitForTaskAssignment(this.currentBatchId)
        this.currentTaskIds = assignment.taskIds
        if (assignment.resultUrl) {
          this.resultUrl = assignment.resultUrl
          this.progress = 100
          this.generationText = '软装方案提取图已生成'
          if (!silent) this.showMessage('软装方案提取图已生成', 'success')
        } else {
          this.generating = false
          await this.continuePolling(silent)
          return
        }
      } catch (error) {
        this.errorMessage = error.message || '暂时无法查询后台任务'
        this.errorSuggestion = error.suggestion || '请稍后继续查询，无需重复提交。'
        this.errorRetryable = error.terminal ? Boolean(error.retryable) : true
        if (!error.terminal) this.scheduleRecovery()
        if (!silent) this.showMessage(this.errorMessage, error.pending ? 'warning' : 'error')
      } finally {
        this.generating = false
        this.loadPendingTask()
      }
    },
    async continuePolling(silent = false) {
      if (this.generating || !this.currentTaskIds.length) return
      this.generating = true
      this.errorMessage = ''
      this.errorSuggestion = ''
      this.errorRetryable = false
      this.progress = Math.max(20, this.progress)
      this.generationText = '正在恢复方案提取进度'
      try {
        this.resultUrl = await this.pollTask(this.currentTaskIds[0])
        this.progress = 100
        this.generationText = '软装方案提取图已生成'
        if (!silent) this.showMessage('软装方案提取图已生成', 'success')
      } catch (error) {
        this.errorMessage = error.message || '查询软装方案提取任务失败'
        this.errorSuggestion = error.suggestion || '请稍后继续查询。'
        this.errorRetryable = Boolean(error.pending || error.retryable)
        if (!silent) this.showMessage(this.errorMessage, error.pending ? 'warning' : 'error')
      } finally {
        this.generating = false
        this.loadPendingTask()
      }
    },
    async copyText(text) {
      if (!text) return
      try {
        await navigator.clipboard.writeText(text)
        this.showMessage('提示词已复制', 'success')
      } catch (error) {
        this.showMessage('复制失败，请手动选择提示词', 'warning')
      }
    },
    statusText(status) {
      return {
        generating: '生成中',
        completed: '已完成',
        failed: '生成失败'
      }[status] || '等待处理'
    },
    formatRecordTime(value) {
      if (!value) return '时间未知'
      const date = new Date(value)
      if (Number.isNaN(date.getTime())) return '时间未知'
      return new Intl.DateTimeFormat('zh-CN', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        hour12: false
      }).format(date)
    },
    restoreHistoryRecord(record) {
      if (record.source) {
        if (this.previewUrl && this.previewUrl.startsWith('blob:')) URL.revokeObjectURL(this.previewUrl)
        this.previewUrl = record.source
        this.sourceFileName = '历史任务原图'
      }
      this.resultUrl = record.result || ''
      this.submittedPrompt = record.prompt || ''
      this.currentBatchId = record.id
      this.currentTaskIds = record.taskIds || []
      this.errorMessage = ''
      this.errorSuggestion = ''
      if (record.status === 'generating') {
        if (this.currentTaskIds.length) this.continuePolling(true)
        else this.scheduleRecovery(300)
      }
    },
    previewHistoryImage(record, type) {
      const selectedUrl = type === 'source' ? record.source : record.result
      if (!selectedUrl) return
      this.restoreHistoryRecord(record)
      const items = [
        record.source ? { url: record.source, title: '软装方案提取 · 原始效果图' } : null,
        record.result ? { url: record.result, title: '软装方案提取 · AI 方案板' } : null
      ].filter(Boolean)
      const index = Math.max(0, items.findIndex(item => item.url === selectedUrl))
      this.previewImage(selectedUrl, items[index].title, items, index)
    },
    async downloadHistoryResult(record) {
      const previous = this.resultUrl
      this.resultUrl = record.result
      try {
        await this.downloadResult()
      } finally {
        this.resultUrl = previous
      }
    },
    async downloadResult() {
      try {
        const blob = await downloadResource(this.resultUrl)
        const link = document.createElement('a')
        link.href = URL.createObjectURL(blob)
        link.download = `soft-decoration-extraction-${Date.now()}.png`
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
        URL.revokeObjectURL(link.href)
      } catch (error) {
        this.showMessage(error.message || '下载失败，请稍后重试', 'error')
      }
    },
    showMessage(message, type = 'info') {
      this.$message({ message, type, showClose: true, duration: 2800, offset: 24 })
    }
  }
}
</script>

<style scoped>
.soft-extract-workspace { width: calc(100% - 68px); max-width: none; margin: 26px auto 56px; color: #202b25; }
.soft-extract-toolbar { min-height: 84px; display: flex; align-items: flex-start; justify-content: space-between; gap: 28px; margin-bottom: 18px; }
.workspace-kicker { color: #2563eb; font-size: 9px; font-weight: 700; letter-spacing: 2px; }
.soft-extract-toolbar h2, .method-head h2 { margin: 7px 0 5px; color: #202b25; font-size: 19px; }
.soft-extract-toolbar p { margin: 0; color: #818b84; font-size: 11px; line-height: 1.6; }
.api-state { flex: 0 0 auto; display: inline-flex; align-items: center; gap: 7px; margin-top: 8px; border: 1px solid #e1e6e2; border-radius: 5px; padding: 7px 10px; background: #fff; color: #858f89; font-size: 9px; }
.api-state i { width: 6px; height: 6px; border-radius: 50%; background: #bbbcb8; }
.api-state.ready { border-color: #c8ddcf; color: #32754b; }
.api-state.ready i { background: #3b9b61; box-shadow: 0 0 0 3px #e7f4eb; }

.soft-extract-main { min-height: 660px; display: grid; grid-template-columns: 340px minmax(0, 1fr); overflow: hidden; border: 1px solid #e1e6e2; border-radius: 9px; background: #fff; box-shadow: 0 12px 32px rgba(37,51,43,.06); }
.extract-settings { min-width: 0; padding: 22px; border-right: 1px solid #e5e9e6; background: #fafbfa; }
.setting-section + .setting-section { margin-top: 21px; border-top: 1px solid #e7ebe8; padding-top: 19px; }
.section-title { display: flex; align-items: center; gap: 10px; margin-bottom: 12px; }
.section-title > span { width: 26px; height: 26px; flex: 0 0 26px; display: grid; place-items: center; border-radius: 5px; background: #edf4ff; color: #2563eb; font-size: 9px; font-weight: 700; }
.section-title > div { min-width: 0; display: flex; flex-direction: column; gap: 2px; }
.section-title strong { color: #354139; font-size: 12px; }
.section-title small { color: #929b96; font-size: 8px; line-height: 1.45; }
.image-dropzone { height: 172px; display: grid; place-items: center; position: relative; overflow: hidden; border: 1px dashed #cbd3ce; border-radius: 6px; background: #fff; cursor: pointer; transition: border-color .18s ease, background .18s ease; }
.image-dropzone:hover { border-color: #7ca1e4; background: #f7f9fd; }
.image-dropzone:focus-within { border-color: #2563eb; box-shadow: 0 0 0 3px rgba(37,99,235,.14); }
.image-dropzone input { position: absolute; width: 1px; height: 1px; opacity: 0; }
.image-dropzone > img { width: 100%; height: 100%; display: block; object-fit: contain; background: #f2f4f2; }
.dropzone-empty { display: flex; flex-direction: column; align-items: center; gap: 7px; padding: 20px; color: #858f89; text-align: center; }
.dropzone-empty > i { color: #4f72b8; font-size: 25px; }
.dropzone-empty strong { color: #46524b; font-size: 11px; }
.dropzone-empty small { font-size: 8px; line-height: 1.5; }
.replace-image { position: absolute; right: 8px; bottom: 8px; min-height: 30px; border: 0; border-radius: 4px; padding: 6px 10px; background: rgba(43,45,42,.84); color: #fff; font-size: 9px; cursor: pointer; }
.file-rule { min-width: 0; display: flex; align-items: center; gap: 6px; margin-top: 8px; color: #939c96; font-size: 8px; }
.file-rule.valid { color: #39774f; }
.file-rule span { min-width: 0; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.output-row { display: flex; align-items: center; justify-content: space-between; gap: 10px; }
.output-row + .output-row { margin-top: 10px; }
.output-row > span { color: #667169; font-size: 9px; }
.output-row .el-select { width: 174px; }
.extract-items { display: grid; grid-template-columns: repeat(2, 1fr); gap: 7px; margin-top: 12px; }
.extract-items span { min-height: 32px; display: flex; align-items: center; gap: 6px; border: 1px solid #e1e6e2; border-radius: 5px; padding: 0 8px; background: #fff; color: #626d66; font-size: 8px; }
.extract-items i { color: #4f72b8; font-size: 12px; }
.dimension-toggle-row { margin-top: 12px; border-radius: 5px; padding: 9px 10px; background: #f4f6f4; }
.dimension-toggle-row > div { display: flex; flex-direction: column; gap: 2px; }
.dimension-toggle-row > div > span { color: #59665e; font-size: 9px; }
.dimension-toggle-row small { color: #939c96; font-size: 7px; }
.note-field { display: flex; flex-direction: column; gap: 5px; margin-top: 12px; }
.note-field > span { display: flex; justify-content: space-between; color: #67736b; font-size: 9px; }
.note-field em { color: #9aa29d; font-size: 8px; font-style: normal; font-weight: 400; }
.note-field textarea { width: 100%; min-height: 62px; resize: vertical; border: 1px solid #d9dfda; border-radius: 5px; outline: 0; padding: 8px 9px; background: #fff; color: #3d4941; font-family: inherit; font-size: 9px; line-height: 1.55; }
.note-field textarea:focus { border-color: #2563eb; box-shadow: 0 0 0 3px rgba(37,99,235,.1); }
.output-section { display: flex; flex-direction: column; gap: 9px; }
.output-section .section-title { margin-bottom: 2px; }
.generate-report { width: 100%; min-height: 52px; display: grid; grid-template-columns: 26px 1fr; align-items: center; margin-top: 22px; border: 0; border-radius: 6px; padding: 8px 14px; background: #2563eb; color: #fff; box-shadow: 0 8px 19px rgba(37,99,235,.22); cursor: pointer; transition: transform .18s ease, background .18s ease, box-shadow .18s ease; }
.generate-report:hover:not(:disabled) { background: #1d4ed8; transform: translateY(-1px); box-shadow: 0 10px 23px rgba(37,99,235,.28); }
.generate-report:focus-visible, .report-actions button:focus-visible, .prompt-toggle:focus-visible { outline: 3px solid rgba(37,99,235,.25); outline-offset: 2px; }
.generate-report:disabled { opacity: .72; cursor: wait; }
.generate-report > i, .button-spinner { grid-row: 1 / span 2; font-size: 18px; }
.generate-report > span:not(.button-spinner) { text-align: left; font-size: 12px; font-weight: 700; }
.generate-report small { opacity: .72; text-align: left; font-size: 8px; }
.button-spinner { width: 17px; height: 17px; border: 2px solid rgba(255,255,255,.4); border-top-color: #fff; border-radius: 50%; animation: spin .8s linear infinite; }
.generation-progress { height: 3px; margin-top: 7px; overflow: hidden; border-radius: 2px; background: #e1e6e2; }
.generation-progress i { height: 100%; display: block; background: #2563eb; transition: width .4s ease; }

.report-panel { min-width: 0; display: flex; flex-direction: column; padding: 22px; background: #fff; }
.report-head { min-height: 45px; display: flex; align-items: flex-start; justify-content: space-between; gap: 16px; }
.report-head > div:first-child { min-width: 0; display: flex; flex-direction: column; gap: 4px; }
.report-head strong { color: #354139; font-size: 13px; }
.report-head span { color: #929b96; font-size: 9px; }
.report-actions { display: flex; gap: 6px; }
.report-actions button { width: 34px; height: 34px; border: 1px solid #dfe5e1; border-radius: 5px; background: #fff; color: #59665e; cursor: pointer; }
.report-actions button:hover { border-color: #7ca1e4; color: #2563eb; }
.report-stage { min-height: 510px; display: grid; grid-template-columns: minmax(260px, .9fr) minmax(0, 1.1fr); gap: 1px; margin-top: 13px; overflow: hidden; border: 1px solid #dfe5e1; border-radius: 6px 6px 0 0; background: #dfe5e1; }
.source-preview, .report-preview { min-width: 0; min-height: 0; display: grid; place-items: center; position: relative; overflow: hidden; background: #f2f4f2; }
.source-preview > img, .report-preview > img { width: 100%; height: 100%; display: block; object-fit: contain; background: #f2f4f2; }
.source-preview > img.zoomable-stage-image, .report-preview > img.zoomable-stage-image { cursor: zoom-in; transition: filter .18s ease; }
.source-preview > img.zoomable-stage-image:hover, .report-preview > img.zoomable-stage-image:hover { filter: brightness(.96); }
.source-preview > img.zoomable-stage-image:focus-visible, .report-preview > img.zoomable-stage-image:focus-visible { outline: 3px solid #2563eb; outline-offset: -3px; }
.report-preview { background-color: #f7f9f7; background-image: linear-gradient(rgba(138,153,144,.12) 1px, transparent 1px), linear-gradient(90deg, rgba(138,153,144,.12) 1px, transparent 1px); background-size: 24px 24px; }
.stage-label { position: absolute; z-index: 2; left: 10px; top: 10px; border: 1px solid rgba(221,229,224,.95); border-radius: 4px; padding: 5px 7px; background: rgba(255,255,255,.92); color: #667169; font-size: 8px; font-weight: 700; }
.stage-empty { display: flex; flex-direction: column; align-items: center; gap: 8px; padding: 28px; color: #99a19c; text-align: center; }
.stage-empty > i { color: #6f8dca; font-size: 30px; }
.stage-empty strong { color: #59665e; font-size: 11px; }
.stage-empty > span:last-child { max-width: 250px; font-size: 8px; line-height: 1.6; }
.report-blueprint { width: min(88%, 560px); display: flex; flex-direction: column; align-items: center; gap: 9px; padding: 26px; color: #87918b; }
.blueprint-title { width: 44%; height: 10px; margin-bottom: 5px; border-radius: 2px; background: #a9bddd; }
.blueprint-grid { width: 100%; aspect-ratio: 16 / 9; display: grid; grid-template-columns: .9fr 1.1fr; grid-template-rows: 1.1fr .55fr .85fr; gap: 8px; border: 1px solid #dce3df; padding: 9px; background: rgba(255,255,255,.82); box-shadow: 0 12px 30px rgba(37,51,43,.07); }
.blueprint-grid > span { display: flex; gap: 4px; border: 1px solid #e2e7e4; padding: 6px; background: #fafbfa; }
.blueprint-grid i { display: block; flex: 1; border-radius: 2px; background: #dce3df; }
.blueprint-hero { grid-row: 1 / span 2; }
.blueprint-hero i { background: linear-gradient(135deg, #9bb4df 0 36%, #e2e8f1 36% 64%, #6787bf 64%); }
.blueprint-detail i:nth-child(1) { background: #b8c9e6; }
.blueprint-detail i:nth-child(2) { background: #eef2f8; }
.blueprint-detail i:nth-child(3) { background: #8fa5c9; }
.blueprint-palette i:nth-child(1) { background: #f3f6fb; }
.blueprint-palette i:nth-child(2) { background: #c8d7ee; }
.blueprint-palette i:nth-child(3) { background: #a7b7cf; }
.blueprint-palette i:nth-child(4) { background: #667991; }
.blueprint-palette i:nth-child(5) { background: #4f72b8; }
.blueprint-material i { border-radius: 50%; background: #cbd7e8; }
.blueprint-cards { grid-column: 1 / -1; }
.blueprint-cards i:nth-child(even) { background: #edf1f5; }
.report-blueprint strong { color: #5e6962; font-size: 11px; }
.report-blueprint small { font-size: 8px; }
.report-loader { width: 82px; height: 64px; position: relative; margin-bottom: 9px; }
.report-loader i { position: absolute; inset: 0; border: 2px solid #5f82c5; border-radius: 3px; background: rgba(255,255,255,.45); animation: reportPulse 1.6s ease-in-out infinite; }
.report-loader i:nth-child(2) { inset: 8px -8px -8px 8px; opacity: .58; animation-delay: .18s; }
.report-loader i:nth-child(3) { inset: 16px -16px -16px 16px; opacity: .3; animation-delay: .36s; }
.quality-strip { min-height: 50px; display: flex; align-items: center; justify-content: center; flex-wrap: wrap; gap: 7px 16px; border: 1px solid #dfe5e1; border-top: 0; border-radius: 0 0 6px 6px; background: #fafbfa; }
.quality-strip span { display: inline-flex; align-items: center; gap: 4px; color: #686c67; font-size: 8px; }
.quality-strip i { color: #408258; }
.submitted-prompt { margin-top: 12px; overflow: hidden; border: 1px solid #d7e2f5; border-radius: 6px; background: #f7faff; }
.submitted-prompt header { min-height: 48px; display: flex; align-items: center; justify-content: space-between; gap: 12px; padding: 9px 12px; border-bottom: 1px solid #dfe8f7; }
.submitted-prompt header > div { min-width: 0; display: flex; flex-direction: column; gap: 2px; }
.submitted-prompt header span { color: #4f72b8; font-size: 7px; font-weight: 700; letter-spacing: 1.3px; }
.submitted-prompt header strong { color: #35465f; font-size: 10px; }
.submitted-prompt header button { min-height: 30px; display: inline-flex; align-items: center; gap: 5px; border: 1px solid #c9d9f7; border-radius: 4px; padding: 0 9px; background: #fff; color: #2559b2; font-size: 8px; cursor: pointer; }
.submitted-prompt pre { max-height: 150px; margin: 0; overflow: auto; padding: 12px; color: #526176; font-family: "Microsoft YaHei", sans-serif; font-size: 9px; line-height: 1.7; white-space: pre-wrap; word-break: break-word; }
.generation-error { display: flex; align-items: flex-start; gap: 9px; margin-top: 12px; border-left: 3px solid #cc5750; padding: 10px 12px; background: #fff6f4; color: #9b4742; }
.generation-error > div { min-width: 0; display: flex; flex: 1; flex-direction: column; gap: 3px; }
.generation-error strong { font-size: 10px; }
.generation-error span { font-size: 8px; line-height: 1.55; }
.generation-error button { min-height: 32px; flex: 0 0 auto; border: 1px solid #ddb4b0; border-radius: 4px; padding: 0 10px; background: #fff; color: #a04742; font-size: 8px; cursor: pointer; }

.method-panel { margin-top: 22px; border: 1px solid #e1e6e2; border-radius: 9px; background: #fff; box-shadow: 0 10px 28px rgba(37,51,43,.05); }
.method-head { min-height: 78px; display: flex; align-items: center; justify-content: space-between; gap: 16px; padding: 16px 22px; border-bottom: 1px solid #e7ebe8; }
.method-head h2 { font-size: 16px; }
.prompt-toggle { min-height: 36px; display: inline-flex; align-items: center; gap: 6px; border: 1px solid #c9d9f7; border-radius: 5px; padding: 8px 11px; background: #f5f8ff; color: #2559b2; font-size: 9px; cursor: pointer; }
.method-steps { display: grid; grid-template-columns: repeat(4, 1fr); border-bottom: 1px solid #e7ebe8; }
.method-steps article { min-width: 0; min-height: 120px; display: grid; grid-template-columns: 25px 32px 1fr; align-items: flex-start; gap: 8px; padding: 22px 18px; }
.method-steps article + article { border-left: 1px solid #e7ebe8; }
.method-steps article > span { width: 24px; height: 24px; display: grid; place-items: center; border-radius: 50%; background: #edf4ff; color: #2563eb; font-size: 8px; font-weight: 700; }
.method-steps article > i { color: #4f72b8; font-size: 22px; text-align: center; }
.method-steps strong { display: block; color: #3c4941; font-size: 10px; }
.method-steps p { margin: 5px 0 0; color: #8c9690; font-size: 8px; line-height: 1.6; }
.prompt-content { display: grid; grid-template-columns: 1fr 1fr; gap: 14px; padding: 20px 22px 22px; background: #fafbfa; }
.prompt-block { min-width: 0; overflow: hidden; border: 1px solid #e1e6e2; border-radius: 6px; background: #fff; }
.prompt-block header { min-height: 53px; display: flex; align-items: center; justify-content: space-between; gap: 12px; padding: 10px 12px; border-bottom: 1px solid #e7ebe8; }
.prompt-block header > div { min-width: 0; display: flex; flex-direction: column; gap: 2px; }
.prompt-block header span { color: #4f72b8; font-size: 7px; font-weight: 700; letter-spacing: 1.4px; }
.prompt-block header strong { color: #424e46; font-size: 10px; }
.prompt-block header button { width: 30px; height: 30px; flex: 0 0 30px; border: 1px solid #e1e6e2; border-radius: 4px; background: #fff; color: #667169; cursor: pointer; }
.prompt-block pre { height: 310px; margin: 0; overflow: auto; padding: 14px; color: #585d58; font-family: "Microsoft YaHei", sans-serif; font-size: 9px; line-height: 1.75; white-space: pre-wrap; word-break: break-word; }
.negative-constraints { grid-column: 1 / -1; display: flex; align-items: center; flex-wrap: wrap; gap: 7px; border-left: 3px solid #2563eb; padding: 10px 12px; background: #f7faff; }
.negative-constraints strong { margin-right: 4px; color: #2559b2; font-size: 9px; }
.negative-constraints span { border: 1px solid #d7e2f5; border-radius: 3px; padding: 4px 7px; background: #fff; color: #60708d; font-size: 8px; }
.prompt-reveal-enter-active, .prompt-reveal-leave-active { transition: opacity .18s ease; }
.prompt-reveal-enter, .prompt-reveal-leave-to { opacity: 0; }

.extraction-history { margin-top: 22px; border: 1px solid #e1e6e2; border-radius: 9px; background: #fff; box-shadow: 0 10px 28px rgba(37,51,43,.05); }
.history-section-head { min-height: 82px; display: flex; align-items: center; justify-content: space-between; gap: 18px; padding: 17px 22px; border-bottom: 1px solid #e7ebe8; }
.history-section-head > div { min-width: 0; }
.history-section-head h2 { margin: 6px 0 3px; color: #202b25; font-size: 16px; }
.history-section-head p { margin: 0; color: #8a948e; font-size: 9px; line-height: 1.55; }
.history-refresh { min-height: 36px; display: inline-flex; align-items: center; gap: 6px; flex: 0 0 auto; border: 1px solid #d7e2f5; border-radius: 5px; padding: 0 11px; background: #f7faff; color: #2559b2; font-size: 9px; cursor: pointer; }
.history-refresh:disabled { opacity: .65; cursor: wait; }
.extraction-history-grid { display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 14px; padding: 18px 22px 22px; }
.extraction-history-grid > article { min-width: 0; overflow: hidden; border: 1px solid #dfe5e1; border-radius: 8px; background: #fff; box-shadow: 0 8px 22px rgba(37,51,43,.05); transition: border-color .18s ease, box-shadow .18s ease, transform .18s ease; }
.extraction-history-grid > article:hover { border-color: #c8d3cb; box-shadow: 0 13px 30px rgba(37,51,43,.09); transform: translateY(-1px); }
.extraction-history-grid > article.active { border-color: #7ca1e4; box-shadow: 0 0 0 2px rgba(37,99,235,.07); }
.history-comparison { width: 100%; height: 148px; display: grid; grid-template-columns: 1fr 1fr; gap: 1px; position: relative; background: #dfe5e1; }
.history-figure { min-width: 0; min-height: 0; display: grid; place-items: center; position: relative; overflow: hidden; border: 0; padding: 0; background: #f2f4f2; cursor: zoom-in; }
.history-figure:disabled { cursor: default; }
.history-figure:focus-visible { z-index: 3; outline: 3px solid #2563eb; outline-offset: -3px; }
.history-comparison img { width: 100%; height: 100%; display: block; object-fit: cover; }
.history-figure > span:not(.history-caption) { display: flex; flex-direction: column; align-items: center; gap: 6px; color: #8b958f; font-size: 8px; }
.history-figure > span:not(.history-caption) i { color: #6f8dca; font-size: 20px; }
.history-caption { position: absolute; left: 6px; bottom: 6px; border-radius: 3px; padding: 4px 6px; background: rgba(27,34,30,.76); color: #fff; font-size: 7px; }
.history-zoom { width: 28px; height: 28px; position: absolute; right: 6px; bottom: 6px; display: grid; place-items: center; border-radius: 4px; background: rgba(17,24,39,.78); color: #fff; font-size: 13px; opacity: 0; transform: translateY(3px); transition: opacity .18s ease, transform .18s ease; }
.history-figure:hover .history-zoom, .history-figure:focus-visible .history-zoom { opacity: 1; transform: translateY(0); }
.history-status { position: absolute; z-index: 2; top: 7px; right: 7px; border-radius: 4px; padding: 4px 7px; background: #eef1ef; color: #657068; font-size: 7px; font-weight: 700; }
.history-status.completed { background: #e8f7ed; color: #25834c; }
.history-status.generating { background: #fff5e8; color: #c87820; }
.history-status.failed { background: #fff0ef; color: #c33a30; }
.history-record-meta { min-height: 82px; display: flex; align-items: center; justify-content: space-between; gap: 10px; padding: 10px 11px; border-bottom: 1px solid #e7ebe8; }
.history-record-meta > div { min-width: 0; display: flex; flex-direction: column; gap: 3px; }
.history-record-meta strong { overflow: hidden; color: #354139; font-size: 10px; text-overflow: ellipsis; white-space: nowrap; }
.history-record-details { display: grid; gap: 3px; margin: 2px 0 0; }
.history-record-details > div { min-width: 0; display: grid; grid-template-columns: 46px minmax(0, 1fr); gap: 7px; align-items: baseline; }
.history-record-details dt { color: #929b96; font-size: 8px; }
.history-record-details dd { min-width: 0; margin: 0; overflow: hidden; color: #657068; font-size: 8px; text-overflow: ellipsis; white-space: nowrap; }
.history-record-meta > button { width: 30px; height: 30px; flex: 0 0 30px; border: 1px solid #dfe5e1; border-radius: 4px; background: #fff; color: #4f72b8; cursor: pointer; }
.history-record-prompt { padding: 10px 11px 11px; background: #fafbfa; }
.history-record-prompt header { display: flex; align-items: center; justify-content: space-between; gap: 8px; margin-bottom: 6px; }
.history-record-prompt header strong { color: #667169; font-size: 8px; }
.history-record-prompt header button { width: 25px; height: 25px; border: 1px solid #dfe5e1; border-radius: 4px; background: #fff; color: #4f72b8; cursor: pointer; }
.history-record-prompt header button:disabled { color: #aeb6b1; cursor: not-allowed; }
.history-record-prompt pre { height: 74px; margin: 0; overflow: auto; color: #727d76; font-family: "Microsoft YaHei", sans-serif; font-size: 8px; line-height: 1.55; white-space: pre-wrap; word-break: break-word; }
.history-empty { min-height: 190px; display: flex; flex-direction: column; align-items: center; justify-content: center; gap: 7px; padding: 28px; color: #929b96; text-align: center; }
.history-empty > i { color: #6f8dca; font-size: 28px; }
.history-empty strong { color: #59665e; font-size: 11px; }
.history-empty span { max-width: 420px; font-size: 8px; line-height: 1.6; }

@keyframes spin { to { transform: rotate(360deg); } }
@keyframes reportPulse { 0%, 100% { transform: translateY(0) scale(.97); opacity: .34; } 50% { transform: translateY(-3px) scale(1.02); opacity: 1; } }
@media (prefers-reduced-motion: reduce) {
  *, *::before, *::after { animation-duration: .01ms !important; animation-iteration-count: 1 !important; transition-duration: .01ms !important; }
}
@media (max-width: 1120px) {
  .soft-extract-main { grid-template-columns: 320px minmax(0, 1fr); }
  .report-stage { min-height: 480px; grid-template-columns: minmax(220px, .9fr) minmax(0, 1.1fr); }
  .method-steps { grid-template-columns: repeat(2, 1fr); }
  .method-steps article:nth-child(3) { border-top: 1px solid #e7ebe8; border-left: 0; }
  .method-steps article:nth-child(4) { border-top: 1px solid #e7ebe8; }
  .extraction-history-grid { grid-template-columns: repeat(2, minmax(0, 1fr)); }
}
@media (max-width: 820px) {
  .soft-extract-workspace { width: calc(100% - 24px); margin-top: 16px; }
  .soft-extract-toolbar { flex-direction: column; gap: 8px; }
  .soft-extract-main { grid-template-columns: 1fr; }
  .extract-settings { border-right: 0; border-bottom: 1px solid #e5e9e6; }
  .report-stage { min-height: 780px; grid-template-columns: 1fr; grid-template-rows: .7fr 1.3fr; }
  .prompt-content { grid-template-columns: 1fr; }
  .prompt-block pre { height: 280px; }
  .history-section-head { align-items: flex-start; flex-direction: column; }
  .history-refresh { width: 100%; justify-content: center; }
}
@media (max-width: 560px) {
  .soft-extract-toolbar h2 { font-size: 16px; }
  .method-head { align-items: flex-start; flex-direction: column; }
  .method-steps { grid-template-columns: 1fr; }
  .method-steps article + article { border-top: 1px solid #e7ebe8; border-left: 0; }
  .report-panel { padding: 14px; }
  .report-stage { min-height: 690px; }
  .quality-strip { align-items: flex-start; flex-direction: column; padding: 12px; }
  .prompt-content { padding: 14px; }
  .extraction-history-grid { grid-template-columns: 1fr; padding: 14px; }
}
</style>
